import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import {
  Code2, Terminal, Cpu, Globe,
  Sparkles, Zap, ArrowRight, Play, Star, Braces, ArrowUpRight
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Process from "../components/Process";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";
import PerformanceWorkshop from "../components/PerformanceWorkshop";
import ComplianceStackGame from "../components/ComplianceStackGame";

const webSteps = [
  {
    step: "01",
    title: "Architecture",
    desc: "Defining the tech stack and data structures for maximum scalability and performance.",
    image: "https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=1200"
  },
  {
    step: "02",
    title: "Frontend Build",
    desc: "Crafting pixel-perfect, highly interactive interfaces using React and Next.js.",
    image: "https://images.unsplash.com/photo-1587620962725-abab7fe55159?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "03",
    title: "Performance",
    desc: "Optimizing for core web vitals, ensuring lightning-fast load times and smooth interactions.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "04",
    title: "Deployment",
    desc: "Secure, scalable deployment with continuous integration and automated testing.",
    image: "https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg?auto=compress&cs=tinysrgb&w=1200"
  }
];

const WEBSITES_LIST = [
  {
    name: "Lewana Hotel Dubai",
    niche: "Luxury Hospitality & Booking Platform",
    url: "https://www.lewanahoteldubai.com/",
    image: "/works/websites/lewanahoteldubai.png",
    tag: "HOSPITALITY"
  },
  {
    name: "Cropflyt Technologies",
    niche: "Agricultural Aviation & Tech Portal",
    url: "https://cropflyt-final.vercel.app/en",
    image: "/works/websites/croptflyt.png",
    tag: "TECH & AGRI"
  },
  {
    name: "Claura",
    niche: "Sleek Lifestyle & Luxury E-Commerce",
    url: "https://claura.in/",
    image: "/works/websites/claura.png",
    tag: "E-COMMERCE"
  },
  {
    name: "White Gloves",
    niche: "Premium Facility Services & Operations",
    url: "https://whitegloves.co.in/",
    image: "/works/websites/whitegloves.png",
    tag: "B2B SERVICES"
  },
  {
    name: "IICA Bhopal",
    niche: "Culinary Arts Academy & Training Center",
    url: "https://iica.co.in/iica-centres/iica-bhopal/",
    image: "/works/websites/iica.png",
    tag: "EDUCATION"
  },
  {
    name: "Suri Enterprises",
    niche: "Industrial Spares & Mechanical Engineering",
    url: "https://www.surienterprises.co/",
    image: "/works/websites/surienterprises.png",
    tag: "MANUFACTURING"
  },
  {
    name: "Gaagar Dairy & Sweets",
    niche: "Creative Branding & Cultural Editorial Hub",
    url: "https://gaagar.in/",
    image: "/works/websites/gaagar.png",
    tag: "CREATIVE & MEDIA"
  }
];

const responsiveWebsites = [
  {
    id: "resp-1",
    title: "Claura Couture",
    niche: "Luxury E-Commerce",
    image: "/works/websites/responsive/claura.png",
  },
  {
    id: "resp-2",
    title: "CroptFlyt",
    niche: "Agritech Platform",
    image: "/works/websites/responsive/croptflyt.png",
  },
  {
    id: "resp-3",
    title: "Gaagar Dairy & Sweets",
    niche: "Bespoke Curation",
    image: "/works/websites/responsive/gaagar.png",
  }
];

const WebDevelopment = () => {
  const [hoveredSite, setHoveredSite] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    window.lenis = lenis;
    return () => {
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO
        title="High-Performance Web Development"
        description="Bespoke web development using React, Next.js, and modern tech stacks. We build the future of the web."
      />
      <div className="grain" />
      <Navbar />

      <main>
        <SectionStepper
          sections={[
            { id: "top", label: "Hero" },
            { id: "capabilities", label: "Capabilities" },
            { id: "portfolio", label: "Websites" },
            { id: "process", label: "Process" },
            { id: "workshop", label: "Workshop" },
            { id: "game", label: "Compliance" }
          ]}
        />
        {/* Hero */}
        <section id="top" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <motion.div
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.55 }}
              transition={{ duration: 1.5 }}
              className="w-full h-full"
            >
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-75"
              >
                <source src="https://videos.pexels.com/video-files/6804109/6804109-uhd_2732_1440_25fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </div>

          <div className="container-xl w-full relative z-20 px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-5xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
                Digital Engineering
              </span>
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mb-8 leading-tight">
                Crafting <br />
                <span className="italic font-serif-accent gold-text">Clean</span> Code
              </h1>
              <p className="max-w-2xl text-[var(--cream-muted)] text-lg md:text-xl mb-12 font-body leading-relaxed">
                We bridge the gap between design and engineering. Our websites aren't just beautiful; they are high-performance machines built for conversion and scale.
              </p>
              <div className="flex flex-col md:flex-row items-start justify-start gap-6">
                <Link to="/contact" className="btn-primary">
                  Deploy Your Vision <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <a href="#portfolio" className="btn-ghost">
                  View Showcase
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section id="capabilities" className="py-32 relative">
          <div className="container-xl px-6">
            <Kicker index="01" label="Capabilities" />
            <h2 className="mt-8 font-display text-4xl md:text-6xl text-[var(--cream)] mb-20">
              Modern <span className="italic gold-text">Stack</span> Solutions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: <Code2 />, title: "Frontend Engineering", desc: "Interactive, responsive, and blazing-fast React/Next.js apps." },
                { icon: <Terminal />, title: "Full-Stack Dev", desc: "Robust backends and seamless API integrations." },
                { icon: <Cpu />, title: "Performance Audit", desc: "Optimizing every line of code for maximum speed." }
              ].map((s, i) => (
                <div key={i} className="p-10 bg-[var(--ink-2)] border border-[var(--rule)] rounded-2xl hover:border-[var(--gold)] transition-all duration-500 group">
                  <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform origin-left">{s.icon}</div>
                  <h3 className="font-display text-2xl mb-4">{s.title}</h3>
                  <p className="text-[var(--muted)] text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6-Site macOS Interactive Website Showcase */}
        <section id="portfolio" className="py-32 bg-[var(--ink-2)] border-y border-[var(--rule)] relative overflow-hidden">
          <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-[radial-gradient(circle_at_center,rgba(201,169,97,0.03)_0%,transparent_70%)] pointer-events-none" />

          <div className="container-xl px-6">
            <Kicker index="02" label="Web Engineering" />

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20">
              <div className="max-w-2xl">
                <h2 className="font-display text-5xl md:text-7xl text-[var(--cream)] leading-tight">
                  Featured <span className="italic gold-text">Deployments</span>
                </h2>
                <p className="mt-6 text-[var(--cream-muted)] text-base md:text-lg font-body leading-relaxed">
                  Real-world enterprise applications designed, optimized, and built by Meraki. Hover over any workspace mockup to autoscroll the full design.
                </p>
              </div>
              <div className="text-right hidden lg:block">
                <span className="font-impact text-[10px] tracking-[0.3em] text-[var(--gold)] uppercase">LAUNCH LABS</span>
              </div>
            </div>

            {/* Grid of 6 Web mockups */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {WEBSITES_LIST.map((web, idx) => (
                <div
                  key={idx}
                  className="bg-black/45 rounded-3xl overflow-hidden border border-white/5 shadow-2xl hover:border-[var(--gold)]/40 hover:shadow-[0_15px_40px_rgba(0,0,0,0.8)] transition-all duration-500 flex flex-col group"
                >
                  {/* macOS window title mockup */}
                  <div className="flex items-center gap-1.5 px-4 py-3 bg-[#0f0e0d] border-b border-white/5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
                    <span className="mx-auto text-[9px] text-[var(--cream-muted)] font-mono truncate max-w-[150px] pl-6">
                      {web.url.replace("https://", "").replace("www.", "")}
                    </span>
                  </div>

                  {/* Browser viewport with autoscroll hover effect */}
                  <div className="relative h-[380px] w-full overflow-hidden bg-[#0d0c0a] border-b border-white/5 flex flex-col justify-start">
                    <img
                      src={web.image}
                      alt={web.name}
                      className="w-full website-screenshot-scroll"
                      style={{ objectPosition: "top", objectFit: "cover" }}
                    />

                    {/* Dark glass overlay reveal on hover */}
                    <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none flex items-center justify-center backdrop-blur-[1px]">
                      <div className="px-4 py-2 bg-black/75 border border-white/10 backdrop-blur-md rounded-full text-[9px] font-impact text-[var(--gold)] uppercase tracking-wider">
                        Scroll Previewing...
                      </div>
                    </div>
                  </div>

                  {/* Meta details bar */}
                  <div className="p-6 flex flex-col justify-between flex-1">
                    <div>
                      <div className="flex items-center justify-between gap-4 mb-2">
                        <span className="font-impact text-[8px] tracking-[0.25em] bg-white/5 border border-white/10 text-[var(--gold)] px-2.5 py-0.5 rounded-md uppercase">
                          {web.tag}
                        </span>
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title="Production Ready" />
                      </div>
                      <h3 className="font-display text-xl text-[var(--cream)] group-hover:text-[var(--gold-bright)] transition-colors duration-300">
                        {web.name}
                      </h3>
                      <p className="mt-2 text-[12px] text-[var(--cream-muted)] leading-relaxed font-body line-clamp-2">
                        {web.niche}. Structured infrastructure engineered for maximum responsiveness, visual identity, and core-web excellence.
                      </p>
                    </div>

                    <a
                      href={web.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 pt-4 border-t border-white/[0.03] flex items-center justify-between text-[var(--cream)] group-hover:text-[var(--gold-bright)] transition-colors duration-300"
                    >
                      <span className="font-impact text-[10px] tracking-widest uppercase">Launch Live Site</span>
                      <div className="w-8 h-8 rounded-full border border-white/10 group-hover:border-[var(--gold)] flex items-center justify-center transition-all duration-300">
                        <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
                      </div>
                    </a>
                  </div>

                </div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <p className="text-[11px] text-[var(--muted)] italic font-body flex items-center justify-center gap-1.5">
                <Sparkles size={12} className="text-[var(--gold)]" /> Hover above any web layout mockups to autoscroll design depth.
              </p>
            </div>

          </div>
        </section>

        {/* Mobile Responsive Showcase */}
        <section className="py-24 bg-[#0a0908] border-b border-[var(--rule)] relative overflow-hidden">
          <div className="container-xl px-6">
            <Kicker index="03" label="Mobile Architecture" />

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20">
              <div className="max-w-2xl">
                <h2 className="font-display text-5xl md:text-7xl text-[var(--cream)] leading-tight">
                  Responsive <span className="italic gold-text">Mockups</span>
                </h2>
                <p className="mt-6 text-[var(--cream-muted)] text-base md:text-lg font-body leading-relaxed">
                  Engineered for seamless viewport adaptation. Hover over any mobile screen to experience the complete responsive layout from header to footer.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 max-w-5xl mx-auto justify-items-center">
              {responsiveWebsites.map((site) => (
                <div
                  key={site.id}
                  className="flex flex-col items-center group w-full"
                  onMouseEnter={() => setHoveredSite(site.id)}
                  onMouseLeave={() => setHoveredSite(null)}
                >
                  {/* iPhone Device Mockup */}
                  <div
                    className="relative w-[260px] h-[520px] rounded-[44px] bg-[#0c0a09] border-[10px] border-[#1d1d1f] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] overflow-hidden transition-transform duration-500 hover:scale-[1.02]"
                    style={{
                      outline: "1px solid rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    {/* Dynamic Island / Notch */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-4 bg-black rounded-full z-30 flex items-center justify-around px-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#111] border border-white/5" />
                      <span className="w-1 h-1 rounded-full bg-[#050505]" />
                    </div>

                    {/* speaker ear piece */}
                    <div className="absolute top-1 left-1/2 -translate-x-1/2 w-12 h-1 bg-black rounded-full z-30" />

                    {/* Viewport Frame */}
                    <div className="w-full h-full relative overflow-hidden rounded-[34px] bg-[#090807]">
                      <img
                        src={site.image}
                        alt={site.title}
                        className="w-full absolute left-0 top-0 transition-transform ease-in-out"
                        style={{
                          objectPosition: "top center",
                          transform: hoveredSite === site.id ? "translateY(calc(-100% + 500px))" : "translateY(0)",
                          transitionDuration: hoveredSite === site.id ? "12000ms" : "2500ms"
                        }}
                      />
                    </div>
                  </div>

                  {/* Device Title & Details */}
                  <div className="mt-6 text-center">
                    <h4 className="font-display text-xl text-[var(--cream)] group-hover:text-[var(--gold)] transition-colors duration-300">
                      {site.title}
                    </h4>
                    <p className="text-[10px] font-impact tracking-wider text-[var(--cream-muted)] uppercase mt-1">
                      {site.niche}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Process steps={webSteps} title="The Development Cycle" kicker={{ index: "04", label: "The Build" }} />

        <PerformanceWorkshop id="workshop" />

        <ComplianceStackGame id="game" />

      </main>
      <Footer />
    </div>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default WebDevelopment;
